import React, {
  useState,
} from 'react';
import clsx from 'clsx';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import IconButton from '@mui/material/IconButton';
import {
  formatPhone,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import {
  useUserProfilePicture,
} from 'pages/Dashboard/hooks/media';
import {
  getAddressString,
} from 'utils/string';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import isNil from 'lodash/isNil';
import Name from 'pages/Dashboard/components/Name';
import UserProfilePictureDialog from 'views/EMR/components/UserProfilePictureDialog';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';

type ProfileRecordProps = {
  title: string;
  value?: string;
};

function ProfileRecord({
  title,
  value = '',
}: ProfileRecordProps) {
  return (
    <div className="flex border-b text-sm">
      <p className="p-4 text-gray-900 font-bold w-48">
        {title}
        :
      </p>
      <p className="p-4">{value}</p>
    </div>
  );
}

export default function Profile() {
  const [isProfilePictureDialogOpen, setIsProfilePictureDialogOpen] = useState(false);
  const useInfo = useUserInfo();
  const {
    user,
    organization,
  } = useInfo ?? {};
  const userId = user?.userId ?? 0;

  const { canRequestMultimedia: canUploadMultimedia } = useEMRAccess();

  const { src: profilePictureUrl, isFetching: isProfilePictureLoading } = useUserProfilePicture(
    userId,
  );

  const serviceFacilities = useServiceFacilities();
  const {
    name: organizationName,
    organizationPhoneNumber: phoneNumber,
    organizationEmail: email,
    eFaxConfig,
    practiceType,
    timeZoneId,
  } = organization ?? {};
  const principalFacility = (serviceFacilities ?? []).find(
    (serviceFacility) => serviceFacility.isPrincipal,
  );
  const hasProfilePicture = !isNil(profilePictureUrl);
  const profilePictureTooltip = hasProfilePicture ? 'View/Edit Profile picture' : 'Upload profile picture';

  return (
    <div className="flex flex-col w-full">
      {!isNil(useInfo) ? (
        <div className="relative overflow-x-auto flex-1">
          <div className="w-full text-left text-gray-500">
            <div className="relative">
              <Name
                {...(user?.formattedNames ?? {})}
                image={{
                  variant: 'rounded',
                  size: 'lg',
                  src: profilePictureUrl ?? undefined,
                }}
                classes={{
                  root: 'p-4 bg-gray-100 peer/profile',
                  nameRoot: '!overflow-visible !whitespace-normal uppercase',
                  label: 'font-semibold text-xl uppercase',
                }}
              >
                <div className="flex flex-col w-full">
                  <small className="text-gray-500">{user?.email}</small>
                </div>
              </Name>
              {canUploadMultimedia && (
                <div className={clsx('absolute left-8 top-8 opacity-0 hover:!opacity-100 peer-hover/profile:!opacity-100 peer-focus-visible/profile:!opacity-100', {
                  '!opacity-100': isProfilePictureLoading,
                })}
                >
                  <ToolTip title={profilePictureTooltip}>
                    <IconButton
                      className="!p-1 !bg-black/50 hover:!bg-black/60 !rounded-lg !w-8 !h-8"
                      disableRipple
                      onClick={() => { setIsProfilePictureDialogOpen(true); }}
                      disabled={isProfilePictureLoading || userId <= 0}
                    >
                      {isProfilePictureLoading
                        ? <Spinner className="!text-white !w-5 !h-5" />
                        : <RemoveRedEyeOutlinedIcon className="!text-white !w-5 !h-5" />}
                    </IconButton>
                  </ToolTip>
                </div>
              )}
            </div>
            <div>
              <ProfileRecord
                title="Organization Name"
                value={organizationName ?? ''}
              />
              {!isNil(organization) && (
                <>
                  <ProfileRecord
                    title="Principal Address"
                    value={getAddressString(principalFacility?.address, false, true)}
                  />
                  <ProfileRecord
                    title="Phone"
                    value={formatPhone(phoneNumber?.phoneNumberWithAreaCode ?? '')}
                  />
                  <ProfileRecord
                    title="Email"
                    value={email ?? '—'}
                  />
                  {!isNil(eFaxConfig) && (
                    <ProfileRecord
                      title="Fax Number"
                      value={formatPhone(eFaxConfig?.callerId ?? '')}
                    />
                  )}
                </>
              )}
              {!isNil(practiceType) && (
                <ProfileRecord
                  title="Practice Type"
                  value={practiceType ?? ''}
                />
              )}
              {!isNil(timeZoneId) && (
                <ProfileRecord
                  title="Time Zone"
                  value={timeZoneId ?? ''}
                />
              )}
            </div>
          </div>
          {isProfilePictureDialogOpen && (
            <UserProfilePictureDialog
              userId={userId}
              onClose={() => { setIsProfilePictureDialogOpen(false); }}
            />
          )}
        </div>
      ) : (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
}
